/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import React, { Component, RefObject } from 'react';
import CrosstabLevelsPart from './CrosstabLevelsPart';
import { IMultiAxisDataset, IMultiAxisDatasetHandle } from '../../../../store/types/MultiAxisDatasetTypes';
import { ICrosstabVisualizationDynamicProperties } from '../types/CrosstabVisualizationTypes';

interface ICrosstabProbeLayoutManagerProps {
    dataset: IMultiAxisDataset,
    handle: IMultiAxisDatasetHandle,
    staticProperties: any,
    dynamicProperties: ICrosstabVisualizationDynamicProperties
}

export default class CrosstabProbeLayoutManager extends Component<ICrosstabProbeLayoutManagerProps> {
    crosstabRef: RefObject<HTMLDivElement>;

    levelsPartContainerRef: RefObject<HTMLDivElement>;

    constructor(a: any, b: any) {
        super(a, b);

        this.crosstabRef = React.createRef<HTMLDivElement>();
        this.levelsPartContainerRef = React.createRef<HTMLDivElement>();
    }

    componentDidMount() {
        this.postRenderOperations();
    }

    componentDidUpdate() {
        this.postRenderOperations();
    }

    setCentralBorderSize(dynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        const element = this.levelsPartContainerRef.current;
        let centralBorderWidth = 1;

        if (element && window.getComputedStyle) {
            const cell = element.getElementsByClassName('jr-mDatatable-cell')[0];
            if (cell) {
                centralBorderWidth = +window.getComputedStyle(cell).borderRightWidth.slice(0, -2);
            }
        }

        return {
            ...dynamicProps,
            centralBorderWidth
        }
    }

    setDefaultMinimalCellSize(dynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        const element = this.levelsPartContainerRef.current;
        const res = dynamicProps;

        if (element) {
            res.minimalCellHeight = 22;
            const columnLabelCell = element.getElementsByClassName('jr-mDatatable-cellColumnlabel');
            if (columnLabelCell[0]) {
                res.minimalCellHeight = columnLabelCell[0].clientHeight;
            }
            res.minimalCellWidth = res.minimalCellHeight * 5;
        }

        return res;
    }

    positionParts(newDynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        let res = newDynamicProps;
        if (this.crosstabRef.current && this.levelsPartContainerRef.current) {
            const {
                current: {
                    clientWidth: crosstabWidth,
                    clientHeight: crosstabHeight
                }
            } = this.crosstabRef;

            const { width, height } = this.levelsPartContainerRef.current.getBoundingClientRect();
            const borderWidth = this.props.dynamicProperties.centralBorderWidth || newDynamicProps.centralBorderWidth;

            res = {
                ...newDynamicProps,
                crosstabWidth,
                crosstabHeight,
                xPointX: Math.ceil(width) + borderWidth,
                xPointY: Math.ceil(height)
            }
        }
        return res;
    }

    alignRowHeaders(dynamicProps: ICrosstabVisualizationDynamicProperties): ICrosstabVisualizationDynamicProperties {
        let res = dynamicProps;

        if (this.levelsPartContainerRef.current) {
            const headerWidths: number[] = [];
            const top = this.levelsPartContainerRef.current.getElementsByClassName('jr-mDatatable-cellColumnlabel');

            for (let i = 0; i < top.length; i += 1) {
                headerWidths.push(Math.ceil(top[i].getBoundingClientRect().width));
            }

            res = { ...res, headerWidths };
        }

        return res;
    }

    postRenderOperations() {
        let dynamicProps = {} as ICrosstabVisualizationDynamicProperties;

        dynamicProps = this.setCentralBorderSize(dynamicProps)
        dynamicProps = this.setDefaultMinimalCellSize(dynamicProps);
        dynamicProps = this.alignRowHeaders(dynamicProps);
        dynamicProps = this.positionParts(dynamicProps);

        this.props.handle.updateDynamicProperties(dynamicProps)
    }

    render() {
        const {
            dataset,
            staticProperties: {
                levelsStructure
            },
            dynamicProperties: {
                headerWidths
            }
        } = this.props;

        return (
            <div ref={this.crosstabRef} className="jr-mDatatableCrosstabContainer jr">
                <div className="jr-mDatatableCrosstab-headerContainer jr">
                    <div ref={this.levelsPartContainerRef} className="jr-mDatatableCrosstab-header-rowlabelsContainer jr">
                        <CrosstabLevelsPart
                            dataset={dataset}
                            levelsStructure={levelsStructure}
                            headerWidths={headerWidths}
                            onLevelExpansionChange={() => {}}
                        />
                    </div>
                </div>
                <div className="jr-mDatatableCrosstab-bodyContainer jr" />
            </div>
        )
    }
}
